import React from "react";
import { Link } from "react-router-dom";
import * as Phosphor from "@phosphor-icons/react";
import { Check, ArrowRight } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Overline, Container, btn } from "@/components/ui-kit";
import { IMAGES } from "@/lib/content";

const Icon = ({ name, ...props }) => {
  const C = Phosphor[name] || Phosphor.Circle;
  return <C {...props} />;
};

const LAND_IMG =
  "https://images.unsplash.com/photo-1685488919515-5bd4f01b0b93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjB1bmRldmVsb3BlZCUyMGdyZWVuJTIwbGFuZCUyMGZvcmVzdCUyMGZpZWxkc3xlbnwwfHx8fDE3ODY2NTc1OTl8MA&ixlib=rb-4.1.0&q=85";

const PROPERTY_TYPES = [
  { icon: "Buildings", title: "Residential Development Land", desc: "Well-located tracts positioned for new residential communities." },
  { icon: "TreeEvergreen", title: "Vacant Acreage", desc: "Undeveloped land in or near the path of growth." },
  { icon: "SquaresFour", title: "Large Residential Parcels", desc: "Sizable holdings with scale for master-planned potential." },
  { icon: "Plant", title: "Farms with Development Potential", desc: "Agricultural land transitioning toward residential use." },
  { icon: "PuzzlePiece", title: "Infill Opportunities", desc: "Underused parcels within established, growing areas." },
  { icon: "Scroll", title: "Estate & Inherited Properties", desc: "Legacy and family-owned land ready for a new chapter." },
  { icon: "Intersect", title: "Assemblage Opportunities", desc: "Adjacent parcels that create greater combined value." },
  { icon: "ArrowsClockwise", title: "Underutilized Properties", desc: "Land not being used to its highest and best potential." },
  { icon: "EyeSlash", title: "Off-Market Properties", desc: "Discreet opportunities before they reach the broader market." },
  { icon: "Gavel", title: "Properties Requiring Rezoning", desc: "Parcels whose value can be unlocked through entitlement." },
  { icon: "TrendUp", title: "Future Development Potential", desc: "Land positioned for value as the region continues to grow." },
];

const CRITERIA = [
  { icon: "MapPin", title: "Location", desc: "Position within growth corridors and demand centers." },
  { icon: "Ruler", title: "Acreage", desc: "Size and shape relative to development potential." },
  { icon: "Path", title: "Road Frontage", desc: "Frontage and visibility along public roadways." },
  { icon: "MapTrifold", title: "Zoning", desc: "Current designation and rezoning opportunity." },
  { icon: "Drop", title: "Water Availability", desc: "Access to public water or viable well solutions." },
  { icon: "FlowArrow", title: "Sewer Availability", desc: "Public sewer proximity or septic feasibility." },
  { icon: "HouseLine", title: "Nearby Development", desc: "Surrounding communities and active projects." },
  { icon: "SignpostSimple", title: "Access", desc: "Ingress, egress, and connectivity to the site." },
  { icon: "ChartLineUp", title: "Market Demand", desc: "Local absorption and residential demand trends." },
  { icon: "Sparkle", title: "Development Potential", desc: "Overall feasibility and long-term upside." },
];

export default function WhatWeBuy() {
  return (
    <div data-testid="what-we-buy-page">
      <Seo
        title="What We Buy — Land & Development Opportunities"
        description="Redwood Capital buys residential development land, vacant acreage, large parcels, farms, infill, estate and inherited properties, assemblages, off-market land, and properties requiring rezoning across Charlotte and Union County, NC."
        path="/what-we-buy"
      />
      <PageHero
        overline="What We Buy"
        title="We're Looking for Real Estate Opportunities"
        subtitle="Redwood Capital acquires land across the Charlotte region. If you own property with residential development potential — listed or off-market — we would like to learn more about it."
        image={IMAGES.heroHome}
      />

      {/* PROPERTY TYPES — visual cards */}
      <section className="py-20 md:py-28" data-testid="property-types">
        <Container>
          <Reveal>
            <Overline>Properties We Buy</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight max-w-3xl">
              The types of land and property we pursue
            </h2>
            <p className="mt-5 text-ink text-lg leading-relaxed max-w-2xl">
              We evaluate a broad range of residential land opportunities across the Charlotte metro and surrounding
              high-growth markets — each on its own merits.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROPERTY_TYPES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="relative h-full bg-white border border-line rounded-sm p-8 overflow-hidden transition-all duration-300 hover:border-gold hover:shadow-[0_18px_44px_rgba(13,46,27,0.10)] hover:-translate-y-1 group">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-forest/5 group-hover:bg-gold/15 transition-colors duration-300">
                      <Icon name={p.icon} size={26} weight="light" className="text-forest" />
                    </div>
                    <span className="font-serif text-3xl text-forest/5 group-hover:text-gold/25 transition-colors duration-300 select-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-forest">{p.title}</h3>
                  <p className="mt-3 text-ink leading-relaxed text-[15px]">{p.desc}</p>
                  <span className="mt-5 block h-px w-0 bg-gold group-hover:w-12 transition-all duration-500" />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHAT MAKES A PROPERTY INTERESTING */}
      <section className="relative bg-forest text-white py-20 md:py-32 overflow-hidden" data-testid="property-criteria">
        <img src={IMAGES.blueprint} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="grain" />
        <Container className="relative">
          <div className="max-w-2xl">
            <Reveal>
              <Overline>Our Criteria</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                What Makes a Property Interesting to Us?
              </h2>
              <p className="mt-5 text-white/60 text-lg leading-relaxed">
                Every opportunity is unique. These are the factors we weigh most carefully when evaluating land.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {CRITERIA.map((c, i) => (
              <Reveal key={c.title} delay={(i % 5) * 0.05}>
                <div className="h-full bg-forest p-7 hover:bg-forest-light transition-colors duration-300 group">
                  <Icon name={c.icon} size={26} weight="light" className="text-gold group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="mt-4 font-serif text-lg font-semibold text-white">{c.title}</h3>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* DOESN'T FIT NOTE */}
      <section className="py-16 md:py-20" data-testid="doesnt-fit">
        <Container>
          <Reveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 text-forest font-medium">
                <Check size={20} weight="bold" className="text-gold" />
                <span className="text-lg">Not sure your property fits?</span>
              </span>
              <p className="mt-3 text-ink text-lg leading-relaxed">
                Have a property that doesn't perfectly fit these categories? Send it to us anyway — we evaluate
                opportunities individually.
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* HAVE LAND TO SELL — prominent CTA */}
      <section className="relative overflow-hidden bg-forest" data-testid="have-land-to-sell">
        <img src={LAND_IMG} alt="Undeveloped land with forest and open fields" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/90 to-forest/60" />
        <div className="grain" />
        <Container className="relative py-20 md:py-28">
          <div className="max-w-3xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-gold">
                <span className="h-px w-8 bg-gold" /> Sell Your Land
              </span>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight text-balance">
                Have Land to Sell?
              </h2>
              <p className="mt-6 text-white/75 text-lg lg:text-xl leading-relaxed max-w-2xl">
                Tell us about your property. Our team will review the opportunity and follow up if we'd like to
                learn more.
              </p>
              <div className="mt-10">
                <Link to="/submit-property" className={`${btn.gold} shadow-[0_10px_30px_rgba(201,168,117,0.35)]`} data-testid="whatwebuy-submit-btn">
                  Submit Your Property <ArrowRight size={18} />
                </Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
